// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import moment from 'moment/moment';

interface AppointmentData {
  id?: string;
  appointmentDate: string;
  appointmentStartTime: number;
  appointmentEndTime?: number;
  status?: string;
  notes?: string;
  deal?: { name?: string };
  user?: { name?: string };
  setter?: { name?: string };
}

interface DealAppointmentsProps {
  appointments: AppointmentData[];
}

function formatScheduleDate(date: string, minutesPastMidnight: number): string {
  const zeroPad = (num: number, places: number) =>
    String(num).padStart(places, '0');
  const hours = Math.trunc(minutesPastMidnight / 60);
  const minutes = minutesPastMidnight - hours * 60;
  const timeStr = `${zeroPad(hours, 2)}:${zeroPad(minutes, 2)}`;
  const momentDate = moment(`${date}T${timeStr}`);
  return momentDate.format('dddd, MMMM Do, h:mm a');
}

function getStatusColor(status: string | undefined): string {
  switch (status) {
    case 'scheduled':
      return '#2196F3';
    case 'completed':
      return '#4CAF50';
    case 'cancelled':
      return '#F44336';
    case 'rescheduled':
      return '#FF9800';
    default:
      return '#9E9E9E';
  }
}

const DealAppointments: React.FC<DealAppointmentsProps> = ({
  appointments,
}) => {
  const { theme } = useTheme();

  if (!appointments || appointments.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 5,
        }}
      >
        <CalendarIcon sx={{ fontSize: 48, color: theme.textLight, mb: 2 }} />
        <Typography sx={{ color: theme.textLight, fontSize: 14 }}>
          No appointments yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {appointments.map((appointment, i) => (
        <Box
          key={appointment.id || i}
          sx={{
            backgroundColor: theme.background3,
            borderRadius: 2,
            p: 2,
            mb: 1.5,
            borderLeft: `4px solid ${getStatusColor(appointment.status)}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, color: theme.text }} />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.text,
                }}
              >
                {formatScheduleDate(
                  appointment.appointmentDate,
                  appointment.appointmentStartTime
                )}
              </Typography>
            </Box>
            {appointment.status && (
              <Chip
                label={
                  appointment.status.charAt(0).toUpperCase() +
                  appointment.status.slice(1)
                }
                size="small"
                sx={{
                  backgroundColor: `${getStatusColor(appointment.status)}20`,
                  color: getStatusColor(appointment.status),
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            )}
          </Box>
          {appointment.deal?.name && (
            <Typography sx={{ fontSize: 13, color: theme.textLight, ml: 3.5 }}>
              {appointment.deal.name}
            </Typography>
          )}
          {appointment.user?.name && (
            <Typography sx={{ fontSize: 13, color: theme.textLight, ml: 3.5 }}>
              Assigned to {appointment.user.name}
            </Typography>
          )}
          {appointment.setter?.name && (
            <Typography sx={{ fontSize: 13, color: theme.textLight, ml: 3.5 }}>
              Setter: {appointment.setter.name}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default DealAppointments;
